import type { ExtraPackage } from 'src/features/ExtraPackage/api/types'
import {
  UANewbiePackEntryPoint,
  UANewbiePackState,
  type UANewbiePackSnapshot,
} from '../types'

const ALL_ENTRY_POINTS: UANewbiePackEntryPoint[] = [
  UANewbiePackEntryPoint.Banner,
  UANewbiePackEntryPoint.Floating,
  UANewbiePackEntryPoint.DailyClaim,
  UANewbiePackEntryPoint.CreditsPage,
]

const DEBUG_PACK_IDS = {
  starter: 'debug-pack-starter',
  creator: 'debug-pack-creator',
  pro: 'debug-pack-pro',
} as const

const TIER_STARTER = {
  tierCode: 'starter',
  itemId: DEBUG_PACK_IDS.starter,
  purchased: false,
  purchasable: true,
}
const TIER_CREATOR = {
  tierCode: 'creator',
  itemId: DEBUG_PACK_IDS.creator,
  purchased: false,
  purchasable: true,
}
const TIER_PRO = {
  tierCode: 'pro',
  itemId: DEBUG_PACK_IDS.pro,
  purchased: false,
  purchasable: true,
}

export type SnapshotPresetKey =
  | 'not_eligible'
  | 'not_triggered'
  | 'active_floating'
  | 'active_aha_shown'
  | 'active_persistent'
  | 'completed'

export const UA_NEWBIE_PACK_SNAPSHOT_PRESETS: Record<
  SnapshotPresetKey,
  UANewbiePackSnapshot
> = {
  not_eligible: {
    state: UANewbiePackState.NotEligible,
    triggerAt: null,
    hoursSinceTrigger: null,
    ahaShownAt: null,
    tiers: [],
    visibleEntryPoints: [],
    promotionSuppression: false,
  },
  not_triggered: {
    state: UANewbiePackState.NotTriggered,
    triggerAt: null,
    hoursSinceTrigger: null,
    ahaShownAt: null,
    tiers: [TIER_STARTER, TIER_CREATOR, TIER_PRO],
    visibleEntryPoints: [],
    promotionSuppression: false,
  },
  active_floating: {
    state: UANewbiePackState.Active48h,
    triggerAt: new Date(Date.now() - 60 * 60 * 1000).toISOString(),
    hoursSinceTrigger: 1,
    ahaShownAt: null,
    tiers: [TIER_STARTER, TIER_CREATOR, TIER_PRO],
    visibleEntryPoints: ALL_ENTRY_POINTS,
    promotionSuppression: false,
  },
  active_aha_shown: {
    state: UANewbiePackState.Active48h,
    triggerAt: new Date(Date.now() - 6 * 60 * 60 * 1000).toISOString(),
    hoursSinceTrigger: 6,
    ahaShownAt: new Date().toISOString(),
    tiers: [TIER_STARTER, TIER_CREATOR, TIER_PRO],
    visibleEntryPoints: [
      UANewbiePackEntryPoint.Banner,
      UANewbiePackEntryPoint.DailyClaim,
      UANewbiePackEntryPoint.CreditsPage,
    ],
    promotionSuppression: false,
  },
  active_persistent: {
    state: UANewbiePackState.ActivePersistent,
    triggerAt: new Date(Date.now() - 72 * 60 * 60 * 1000).toISOString(),
    hoursSinceTrigger: 72,
    ahaShownAt: new Date(Date.now() - 60 * 60 * 60 * 1000).toISOString(),
    tiers: [TIER_STARTER, TIER_CREATOR, TIER_PRO],
    visibleEntryPoints: [UANewbiePackEntryPoint.CreditsPage],
    promotionSuppression: false,
  },
  completed: {
    state: UANewbiePackState.Completed,
    triggerAt: new Date(Date.now() - 96 * 60 * 60 * 1000).toISOString(),
    hoursSinceTrigger: 96,
    ahaShownAt: new Date(Date.now() - 80 * 60 * 60 * 1000).toISOString(),
    tiers: [
      { ...TIER_STARTER, purchased: true, purchasable: false },
      { ...TIER_CREATOR, purchased: true, purchasable: false },
      { ...TIER_PRO, purchased: true, purchasable: false },
    ],
    visibleEntryPoints: [],
    promotionSuppression: false,
  },
}

function makePack(args: {
  id: string
  credits: number
  priceUsd: number
  packNameI18nKey?: string
  packName: string
  valueRatioPercent: number
  totalValueTextI18nKey?: string
  totalValueText: string
  /** Raw count for the V3 "≈ Nx images" chip. */
  equivalentImages?: number
  contents: ExtraPackage['packageContents']
}): ExtraPackage {
  return {
    id: args.id,
    // Newbie packs are flat across membership tiers — mirror the real server
    // shape (4 tier entries) so consumers reading `pack.values[selectedTier]`
    // don't crash for tier-1+ users.
    values: [0, 1, 2, 3].map(tier => ({
      tier,
      value: args.credits,
      creditsAfterBoosts: args.credits,
      boosts: [],
    })),
    prices: {
      USD: args.priceUsd,
      JPY: Math.round(args.priceUsd * 150),
      EUR: args.priceUsd,
    },
    oneTimePurchase: true,
    marketingMetadata: {
      card: {
        packNameI18nKey: args.packNameI18nKey,
        packName: args.packName,
        valueRatioPercent: args.valueRatioPercent,
        totalValueTextI18nKey: args.totalValueTextI18nKey,
        totalValueText: args.totalValueText,
        equivalentImages: args.equivalentImages,
      },
    },
    packageContents: args.contents,
  }
}

const PACK_STARTER = makePack({
  id: DEBUG_PACK_IDS.starter,
  credits: 50_000,
  priceUsd: 1,
  packNameI18nKey: 'membership:ua.pack.starter.name',
  packName: 'Starter Pack',
  valueRatioPercent: 200,
  totalValueTextI18nKey: 'membership:ua.pack.starter.total_value',
  totalValueText: '150K credits',
  equivalentImages: 100,
  contents: [],
})

const PACK_CREATOR = makePack({
  id: DEBUG_PACK_IDS.creator,
  credits: 200_000,
  priceUsd: 6,
  packNameI18nKey: 'membership:ua.pack.creator.name',
  packName: 'Creator Pack',
  valueRatioPercent: 553,
  totalValueTextI18nKey: 'membership:ua.pack.creator.total_value',
  totalValueText: '640K credits',
  equivalentImages: 1200,
  contents: [
    {
      kind: 'kaisuuken',
      code: 'free-image-any',
      quantity: 5,
      metadata: {
        displayNameI18nKey: 'membership:ua.content.reference_pro',
        displayName: 'Reference Pro',
      },
    },
    {
      kind: 'kaisuuken',
      code: 'free-video-any',
      quantity: 5,
      metadata: {
        displayNameI18nKey: 'membership:ua.content.video_generation',
        displayName: 'Video Generation',
      },
    },
  ],
})

const PACK_PRO_WITH_DECORATION = makePack({
  id: DEBUG_PACK_IDS.pro,
  credits: 450_000,
  priceUsd: 12,
  packNameI18nKey: 'membership:ua.pack.pro.name',
  packName: 'Pro Pack',
  valueRatioPercent: 790,
  totalValueTextI18nKey: 'membership:ua.pack.pro.total_value',
  totalValueText: '1896K credits',
  equivalentImages: 2700,
  contents: [
    {
      kind: 'kaisuuken',
      code: 'common-edit-pro',
      quantity: 10,
      metadata: {
        displayNameI18nKey: 'membership:ua.content.edit_pro',
        displayName: 'Edit Pro',
      },
    },
    {
      kind: 'kaisuuken',
      code: 'free-video-any',
      quantity: 10,
      metadata: {
        displayNameI18nKey: 'membership:ua.content.video_generation',
        displayName: 'Video Generation',
      },
    },
    {
      kind: 'decoration',
      code: 'starter_pack_2026_badge_1',
      quantity: 1,
    },
  ],
})

export type PacksPresetKey =
  | '1_starter'
  | '2_starter_creator'
  | '3_with_decoration'

export const UA_NEWBIE_PACK_PACKS_PRESETS: Record<
  PacksPresetKey,
  ExtraPackage[]
> = {
  '1_starter': [PACK_STARTER],
  '2_starter_creator': [PACK_STARTER, PACK_CREATOR],
  '3_with_decoration': [PACK_STARTER, PACK_CREATOR, PACK_PRO_WITH_DECORATION],
}
